import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Wand20Regular, Dismiss20Regular, ArrowClockwise16Regular } from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer, Textarea, Button } from '@fluentui/react-components';
import { InitializeAIChatWithIRL, SendAIMessage, GetAIChatMessages, ClearAIChat } from '../../redux/questionBankSlice';
import axiosInstance from '../../apiServices/axios';
import { toast } from 'react-toastify';
import styles from './css/index.module.css';

const AskAISidebar = ({ show, setShow, selectedRows }) => {
    const [aiMessage, setAiMessage] = React.useState('');
    const dispatch = useDispatch();
    const chatContainerRef = React.useRef(null);

    const { aiChat, aiChatMessages, aiChatLoading, aiChatError } = useSelector(state => state.questionBank);

    // Function to scroll to bottom of chat
    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    // Scroll to bottom when messages change
    React.useEffect(() => {
        scrollToBottom();
    }, [aiChatMessages, aiChatLoading]);

    React.useEffect(() => {
        // Load messages when sidebar opens (chat object is managed by InitializeAIChatWithIRL/SendAIMessage)
        if (show && localStorage['project_id']) {
            dispatch(GetAIChatMessages());
        }
    }, [show, dispatch]);

    React.useEffect(() => {
        // Show error toast if there's an error
        if (aiChatError) {
            toast.error(aiChatError);
        }
    }, [aiChatError]);

    const handleSendAIMessage = async () => {
        if (aiMessage.trim()) {
            const messageContent = aiMessage.trim();
            setAiMessage('');

            // Create a temporary user message to show immediately
            const tempUserMessage = {
                id: `temp-${Date.now()}`,
                role: 'user',
                content: messageContent,
                created_at: new Date().toISOString(),
                is_temp: true, // Flag to identify temporary messages
            };

            // Add user message to chat immediately
            dispatch({
                type: 'questionBankSlice/addTempMessage',
                payload: tempUserMessage,
            });

            try {
                if (!aiChat && (!aiChatMessages || aiChatMessages.length === 0)) {
                    // Initialize chat with IRL data if no chat exists and no messages
                    await dispatch(
                        InitializeAIChatWithIRL({
                            item_ids: selectedRows || [],
                            message: messageContent,
                        }),
                    ).unwrap();
                } else {
                    // Send message to existing chat (either aiChat exists or we have messages)
                    await dispatch(
                        SendAIMessage({
                            message: messageContent,
                            item_ids: selectedRows || [],
                        }),
                    ).unwrap();
                }
            } catch (error) {
                console.error('Failed to send message:', error);

                // Check if the error is "Chat session already exists"
                if (error.message && error.message.includes('Chat session already exists')) {
                    // If chat already exists, try sending message instead
                    try {
                        await dispatch(
                            SendAIMessage({
                                message: messageContent,
                                item_ids: selectedRows || [],
                            }),
                        ).unwrap();
                    } catch (sendError) {
                        console.error('Failed to send message after retry:', sendError);
                        toast.error('Failed to send message. Please try again.');

                        // Remove the temporary message on error
                        dispatch({
                            type: 'questionBankSlice/removeTempMessage',
                            payload: tempUserMessage.id,
                        });
                    }
                } else {
                    toast.error('Failed to send message. Please try again.');

                    // Remove the temporary message on error
                    dispatch({
                        type: 'questionBankSlice/removeTempMessage',
                        payload: tempUserMessage.id,
                    });
                }
            }
        }
    };

    const handleKeyPress = e => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendAIMessage();
        }
    };

    const handleClearChat = async () => {
        try {
            await dispatch(ClearAIChat()).unwrap();
        } catch (error) {
            console.error('Failed to clear chat:', error);
            toast.error('Failed to clear chat');
        }
    };

    const handleRegenerateResponse = async () => {
        // Find the last user message to regenerate the response for
        const userMessages = (aiChatMessages || []).filter(msg => msg.role === 'user');
        if (userMessages.length === 0) {
            toast.error('No user message found to regenerate response for');
            return;
        }

        const lastUserMessage = userMessages[userMessages.length - 1];
        const tempId = `temp-ai-${Date.now()}`; // Move tempId outside try block

        try {
            console.log('Before regenerate - Messages count:', aiChatMessages.length);

            // Remove the last AI response
            dispatch({
                type: 'questionBankSlice/removeLastAIResponse',
            });

            // Create a temporary AI response to show loading
            const tempAIResponse = {
                id: tempId,
                role: 'assistant',
                content: 'Regenerating response...',
                created_at: new Date().toISOString(),
                is_temp: true,
            };

            // Add temporary AI response
            dispatch({
                type: 'questionBankSlice/addTempMessage',
                payload: tempAIResponse,
            });

            console.log('Added temporary response with ID:', tempId); // Debug log

            // Call the backend to regenerate response without adding user message
            const response = await axiosInstance.post(`/tools/gpt_automation/${localStorage['project_id']}/chat/regenerate-response/`, {
                message: lastUserMessage.content,
                item_ids: selectedRows || [],
            });

            console.log('Regenerate API response:', response.data); // Debug log

            // Remove temporary response
            console.log('Removing temporary response with ID:', tempId);
            dispatch({
                type: 'questionBankSlice/removeTempMessage',
                payload: tempId,
            });

            // Add the new AI response
            if (response.data && response.data.ai_response) {
                console.log('Adding AI response to Redux:', response.data.ai_response); // Debug log

                // Check if this response already exists to avoid duplicates
                const existingResponse = aiChatMessages.find(msg => msg.role === 'assistant' && msg.content === response.data.ai_response.content);

                if (!existingResponse) {
                    dispatch({
                        type: 'questionBankSlice/addTempMessage',
                        payload: response.data.ai_response,
                    });
                    console.log('New AI response added to Redux');
                } else {
                    console.log('Response already exists, not adding duplicate');
                }
            } else {
                console.error('No AI response in API response:', response);
                toast.error('No response received from AI');
            }

            console.log('Final state - Messages count:', aiChatMessages.length);
        } catch (error) {
            console.error('Failed to regenerate response:', error);
            toast.error('Failed to regenerate response');

            // Remove temporary response on error
            dispatch({
                type: 'questionBankSlice/removeTempMessage',
                payload: tempId,
            });
        }
    };

    const formatMessageContent = content => {
        if (!content) return '';
        // Convert markdown-style formatting to HTML
        return content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/\n/g, '<br>');
    };

    const formatTimestamp = timestamp => {
        if (!timestamp) return '';
        try {
            return new Date(timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch (error) {
            return '';
        }
    };

    return (
        <OverlayDrawer
            style={{ minWidth: 872 }}
            type="overlay"
            position="end"
            open={show}
            onOpenChange={(_, { open }) => {
                setShow(open);
                if (!open) {
                    setAiMessage('');
                }
            }}>
            <DrawerBody className={`position-relative ${styles.drawerContent}`} style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100vh' }}>
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between px-3 mt-3 mb-3">
                    <div className="d-flex align-items-center">
                        <Wand20Regular color="var(--Brand-Secondary)" className="me-1" />
                        <span className={styles.aiTitle}>Lens Ask AI</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <button
                            onClick={handleClearChat}
                            className="btn btn-outline-secondary btn-sm me-2"
                            disabled={aiChatLoading || !aiChatMessages || aiChatMessages.length === 0}
                            title="Clear chat history">
                            Clear
                        </button>
                        <Dismiss20Regular
                            onClick={() => {
                                setShow(false);
                                setAiMessage('');
                            }}
                            role="button"
                            color="var(--Neutral-Gray-6)"
                        />
                    </div>
                </div>

                {/* Chat Messages */}
                <div ref={chatContainerRef} className="flex-grow-1 px-3" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    {!aiChatMessages || aiChatMessages.length === 0 ? (
                        <div className="text-center text-muted mt-5">
                            <Wand20Regular className="mb-3" style={{ fontSize: '3rem', opacity: 0.3 }} />
                            <h5 className="mb-3 fw-bold">Ask the AI anything.</h5>
                            <p className="mb-3">
                                <strong>The AI will respond using the selected IRL questions and responses as context.</strong>
                            </p>
                            <p className="mb-3">Try asking things like:</p>
                            <div className="text-start" style={{ maxWidth: '356px', margin: '0 auto' }}>
                                <div className="p-3 mb-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    "Summarise the vendor's readiness to scale."
                                </div>
                                <div className="p-3 mb-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    "What are the key risks in their DevOps process?"
                                </div>
                                <div className="p-3 mb-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    "Is there evidence of a product roadmap?"
                                </div>
                            </div>
                            {selectedRows && selectedRows.length > 0 && (
                                <small className="text-muted mt-3 d-block">
                                    {selectedRows.length} item{selectedRows.length > 1 ? 's' : ''} selected
                                </small>
                            )}
                        </div>
                    ) : (
                        (aiChatMessages || []).map((message, index) => {
                            if (!message) return null; // Skip undefined messages
                            const isUser = message.role === 'user';
                            const isAssistant = message.role === 'assistant';
                            const isSystem = message.role === 'system';

                            // Skip system messages - they contain IRL data and persona info
                            if (isSystem) return null;

                            // Check if this is the last AI response
                            const assistantMessages = (aiChatMessages || []).filter(msg => msg.role === 'assistant');
                            const isLastAIResponse = isAssistant && assistantMessages.length > 0 && message.id === assistantMessages[assistantMessages.length - 1].id;

                            return (
                                <div key={message.id} className={`mb-4 position-relative ${isUser ? 'text-end' : 'text-start'}`}>
                                    <div
                                        className={`d-inline-block p-3 rounded ${isUser ? styles.userMessage : isSystem ? 'bg-light border' : styles.assistantMessage}`}
                                        style={{
                                            maxWidth: '80%',
                                            textAlign: 'left',
                                            wordWrap: 'break-word',
                                        }}>
                                        <div
                                            className="mb-2"
                                            dangerouslySetInnerHTML={{
                                                __html: formatMessageContent(message.content || ''),
                                            }}
                                        />
                                        {(isAssistant || isUser) && (
                                            <div className="d-flex justify-content-between align-items-center">
                                                <small style={{ fontSize: 10 }} className={`${isUser ? '' : 'opacity-75'}`}>
                                                    {formatTimestamp(message.created_at || new Date())}
                                                </small>

                                                {/* {isAssistant && message.model_used && (
                                                    <small className="opacity-75 ms-2">
                                                        {message.model_used}
                                                        ({message.tokens_used || 0} tokens)
                                                    </small>
                                                )} */}
                                            </div>
                                        )}
                                    </div>
                                    {isLastAIResponse && (
                                        <ArrowClockwise16Regular
                                            className="position-absolute end-0 mt-3"
                                            role="button"
                                            onClick={handleRegenerateResponse}
                                            color="var(--Brand-Secondary)"
                                        />
                                    )}
                                </div>
                            );
                        })
                    )}

                    {/* Loading indicator */}
                    {aiChatLoading && (
                        <div className="text-start mb-4">
                            <div className="d-inline-block p-3 rounded " style={{ maxWidth: '80%' }}>
                                <div className="d-flex align-items-center">
                                    <div className={styles['typing-dots']}>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input Area */}
                <div className="p-3 " style={{ backgroundColor: 'var(--Neutral-Gray-1)' }}>
                    <div className="d-flex flex-column">
                        <div className="mb-3">
                            <Textarea
                                value={aiMessage}
                                onChange={e => setAiMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Type your question about the selected items ..."
                                style={{
                                    resize: 'vertical',
                                    width: '100%',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    fontSize: '14px',
                                    minHeight: '90px',
                                }}
                                disabled={aiChatLoading}
                            />
                        </div>
                        <div className="d-flex justify-content-start">
                            <button onClick={handleSendAIMessage} className={styles.askAISendButton} disabled={!aiMessage.trim() || aiChatLoading}>
                                {aiChatLoading ? 'Sending...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default AskAISidebar;
